import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useOrganisations } from "../Auth/useOrganisations";

const SupplierStatement = ({ fournisseurId, fournisseurName, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [statement, setStatement] = useState(null);
  const [, , usedOrganisations] = useOrganisations();
  const [dateRange, setDateRange] = useState({
    date_debut: "",
    date_fin: "",
  });

  useEffect(() => {
    if (fournisseurId) {
      loadStatement();
    }
  }, [fournisseurId, dateRange]);

  const loadStatement = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (dateRange.date_debut)
        params.append("date_debut", dateRange.date_debut);
      if (dateRange.date_fin) params.append("date_fin", dateRange.date_fin);

      const response = await axios.get(
        `${process.env.REACT_APP_API}achats/fournisseurs/${fournisseurId}/statement?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        setStatement(response.data.data);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement du relevé");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR");
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(3) + " TND";
  };

  const handleExportPDF = () => {
    // This would call your PDF export endpoint
    window.open(
      `${process.env.REACT_APP_API}achats/fournisseurs/${fournisseurId}/statement/pdf`,
      "_blank",
    );
  };

  if (!fournisseurId) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Relevé Fournisseur - {fournisseurName}
            </h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body">
            {/* Filters */}
            <div className="row mb-3">
              <div className="col-md-4">
                <label>Date Début</label>
                <input
                  type="date"
                  className="form-control"
                  value={dateRange.date_debut}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, date_debut: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4">
                <label>Date Fin</label>
                <input
                  type="date"
                  className="form-control"
                  value={dateRange.date_fin}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, date_fin: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <button
                  className="btn btn-primary mr-2"
                  onClick={loadStatement}
                >
                  <i className="fa fa-search"></i> Filtrer
                </button>
                <button className="btn btn-success" onClick={handleExportPDF}>
                  <i className="fa fa-file-pdf"></i> PDF
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Chargement...</span>
                </div>
              </div>
            ) : statement ? (
              <>
                {/* Summary Cards */}
                <div className="row mb-4">
                  <div className="col-md-4">
                    <div className="card bg-primary text-white">
                      <div className="card-body">
                        <h6>Total Achats</h6>
                        <h4>{formatCurrency(statement.total_achats)}</h4>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-success text-white">
                      <div className="card-body">
                        <h6>Total Paiements</h6>
                        <h4>{formatCurrency(statement.total_paiements)}</h4>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-warning text-white">
                      <div className="card-body">
                        <h6>Solde Actuel</h6>
                        <h4>
                          {formatCurrency(statement.fournisseur?.solde_actuel)}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Supplier Info */}
                <div className="card mb-4">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p>
                          <strong>Nom:</strong> {statement.fournisseur?.nom}
                        </p>
                        <p>
                          <strong>Matricule Fiscal:</strong>{" "}
                          {statement.fournisseur?.matricule_fiscal || "-"}
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p>
                          <strong>Téléphone:</strong>{" "}
                          {statement.fournisseur?.telephone || "-"}
                        </p>
                        <p>
                          <strong>Email:</strong>{" "}
                          {statement.fournisseur?.email || "-"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Transactions Table */}
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Référence</th>
                        <th>Débit (Achat)</th>
                        <th>Crédit (Paiement)</th>
                        <th>Solde</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statement.operations?.map((op, index) => (
                        <tr key={index}>
                          <td>{formatDate(op.date)}</td>
                          <td>
                            {op.type === "achat" ? (
                              <span className="badge badge-primary">
                                Facture
                              </span>
                            ) : (
                              <span className="badge badge-success">
                                Paiement
                              </span>
                            )}
                          </td>
                          <td>{op.reference}</td>
                          <td className="text-right">
                            {op.type === "achat"
                              ? formatCurrency(op.montant)
                              : "-"}
                          </td>
                          <td className="text-right">
                            {op.type === "paiement"
                              ? formatCurrency(Math.abs(op.montant))
                              : "-"}
                          </td>
                          <td className="text-right font-weight-bold">
                            {formatCurrency(op.solde_apres)}
                          </td>
                        </tr>
                      ))}
                      {(!statement.operations ||
                        statement.operations.length === 0) && (
                        <tr>
                          <td colSpan="6" className="text-center">
                            Aucune opération trouvée
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierStatement;
